import { SupportedProviderHelp } from "./SupportedProviderHelp/SupportedProviderHelp";
import { ThemedZeffyLogo } from "@/canvas/layouts/ThemedBranding/ThemedZeffyLogo";
import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { FormType, formatCurrencyFromCents, OTHER_DAF_PROVIDER } from "@simplyk/common";

import { DonationDetails } from "./DonationDetails/DonationDetails";
import { SupportedProviderStep } from "./SupportedProviderStep/SupportedProviderStep";
import { useDafPaymentFlow } from "./helpers/useDafPaymentFlow";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Check } from "@/icons/outlined";

interface DafPaymentFlowProps {
  token: string;
  isResume: boolean;
  emailUpdateToken?: string;
}

export const DafPaymentFlow: FC<DafPaymentFlowProps> = ({ token, isResume, emailUpdateToken }) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const { formObject, isEmbed } = useFormV2Context(FormType.DonationForm);
  const flow = useDafPaymentFlow(token, isResume, emailUpdateToken);
  const { data } = flow;
  if (flow.error) {
    return (
      <FormTypography role="alert" vibe="error">
        {t("common", "daf.linkError")}
      </FormTypography>
    );
  }
  if (!data || data.state === "deleted") {
    return <FormTypography>{t("common", "daf.loading")}</FormTypography>;
  }
  const providerName =
    data.details.providerId === OTHER_DAF_PROVIDER.id ? t("common", "daf.yourFund") : data.details.providerName;
  const amount = formatCurrencyFromCents(data.amount, { isoLocale, currency: data.currency, cents: "hiddenIfZero" });
  const isConfirmation = data.isSupported && flow.showConfirmation;

  if (data.isSupported && flow.providerWindowOpened && !flow.isPhoneScreen && !flow.showConfirmation) {
    return (
      <>
        <SupportedProviderHelp providerName={providerName} amount={amount} />
        {!isEmbed && (
          <Box sx={(theme) => ({ position: "fixed", bottom: theme.spacing(3), right: theme.spacing(3) })}>
            <ThemedZeffyLogo width={99} height={30} />
          </Box>
        )}
      </>
    );
  }

  if (data.isSupported) {
    return (
      <>
        <SupportedProviderStep
          providerName={providerName}
          amount={amount}
          isConfirmation={isConfirmation}
          isEmbed={isEmbed}
          isBusy={flow.isBusy}
          actionError={flow.actionError}
          onOpenProvider={flow.openProvider}
          onConfirm={flow.onConfirm}
          onBack={flow.onBack}
        />
        {!isEmbed && !flow.isPhoneScreen && (
          <Box sx={(theme) => ({ position: "fixed", bottom: theme.spacing(3), right: theme.spacing(3) })}>
            <ThemedZeffyLogo width={99} height={30} />
          </Box>
        )}
      </>
    );
  }

  return (
    <Stack sx={{ minHeight: isEmbed ? "auto" : "100dvh" }}>
      <Stack
        alignItems="center"
        justifyContent="center"
        gap={4}
        paddingY={4}
        flex={1}
        sx={(theme) => ({
          paddingInline: theme.spacing(2),
          width: "100%",
          maxWidth: theme.spacing(64.5),
          marginInline: "auto",
        })}
        data-test="daf-payment-flow"
      >
        <Stack gap={1} textAlign="center" width="100%">
          <FormTypography variant="h5" vibe="brand">
            {t("common", "daf.requestProvider", { provider: providerName })}
            <br />
            {t("common", "daf.requestAmount", { amount })}
          </FormTypography>
          <FormTypography variant="body1" vibe="moderate">
            {t(
              "common",
              data.providerUrl || data.providerEmail || data.details.providerId === OTHER_DAF_PROVIDER.id
                ? "daf.portalRequestDescription"
                : "daf.requestDescription"
            )}
          </FormTypography>
        </Stack>
        <DonationDetails
          details={data.details}
          amount={amount}
          providerName={providerName}
          providerUrl={data.providerUrl}
          providerEmail={data.providerEmail}
          providerGrantFormUrl={data.providerGrantFormUrl}
          providerWebsiteUrl={data.providerWebsiteUrl}
          donorFullName={data.donorFullName}
          logoUrl={formObject.logoUrl ?? formObject.organization?.logoUrl}
          onOpenPortal={flow.openProvider}
          onContactProvider={() => flow.trackAction("contact-provider")}
          onCopy={(field) => flow.trackAction(`copy-${field}`)}
        />
        {flow.actionError && (
          <FormTypography role="alert" vibe="error">
            {t("common", "daf.updateError")}
          </FormTypography>
        )}
        <Stack gap={2} alignItems="center" maxWidth="100%">
          <FormButton
            size="large"
            endIcon={<Check />}
            onClick={flow.onConfirm}
            isLoading={flow.isBusy}
            components={{ endIcon: { sx: { marginInlineStart: 0.5 } } }}
            sx={(theme) => ({
              ...theme.typography.subtitle1,
              boxShadow: theme.boxShadow.base.strong,
              maxWidth: "100%",
              whiteSpace: "normal",
            })}
            data-test="daf-confirm"
          >
            {t("common", "daf.confirm")}
          </FormButton>
          <FormButton
            variant="outline"
            sx={{ maxWidth: "100%", whiteSpace: "normal" }}
            onClick={flow.onBack}
            disabled={flow.isBusy}
            data-test="daf-back"
          >
            {t("common", "daf.back")}
          </FormButton>
        </Stack>
      </Stack>
      {!isEmbed && (
        <Box
          sx={(theme) => ({
            position: { md: "fixed" },
            bottom: theme.spacing(3),
            right: theme.spacing(3),
            display: "flex",
            justifyContent: "center",
            paddingBlock: { xs: 2, md: 0 },
            zIndex: 1,
          })}
        >
          <ThemedZeffyLogo width={99} height={30} />
        </Box>
      )}
    </Stack>
  );
};
